import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    this.applyInitialPintryxDropSetRange();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    this.updateInstallments(event.detail.data.html);

    if (this.isPintryxDropSetRangeLocked()) {
      this.applyInitialPintryxDropSetRange();
      return;
    }

    this.updateInstallmentsVariant(event.detail.resource);
    this.applyPintryxDropSetPrice(event.detail.resource);
  };

  applyInitialPintryxDropSetRange() {
    const priceContainer = this.querySelector('[ref="priceContainer"][data-pintryx-drop-set-price="true"]');
    if (!priceContainer) return;

    priceContainer.dataset.pintryxAwaitingEditionPrice = 'true';
    this.updateText(priceContainer.querySelector('.price'), '');
    this.updateText(priceContainer.querySelector('.compare-at-price'), '');
  }

  updateText(element, text) {
    if (element && text != null) element.textContent = text;
  }

  updateInstallments(html) {
    const newInstallments = html.querySelector('product-price .installments');
    const currentInstallments = this.querySelector('.installments');

    if (currentInstallments && newInstallments && currentInstallments.innerHTML !== newInstallments.innerHTML) {
      currentInstallments.replaceWith(newInstallments);
    } else if (currentInstallments && !newInstallments) {
      currentInstallments.remove();
    } else if (!currentInstallments && newInstallments) {
      this.appendChild(newInstallments);
    }
  }

  updateInstallmentsVariant(variant) {
    const installmentVariantInput = this.querySelector('.installments form.payment-terms input[name="id"]');
    const variantId = variant?.id;
    if (!installmentVariantInput || !variantId || installmentVariantInput.value === String(variantId)) return;

    installmentVariantInput.value = variantId;
    installmentVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  isPintryxDropSetRangeLocked() {
    return this.dataset.pintryxEditionChosen !== 'true';
  }

  /**
   * Applies fixed product-page display prices for the Pintryx Drop Set edition selector.
   * The server still owns the initial price range; this keeps variant updates deterministic.
   * @param {VariantUpdateEvent['detail']['resource']} variant
   */
  applyPintryxDropSetPrice(variant) {
    const priceContainer = this.querySelector('[ref="priceContainer"][data-pintryx-drop-set-price="true"]');
    if (!priceContainer || !variant) return;

    const options = Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3];
    this.applyPintryxDropSetPriceFromText(options.filter(Boolean).join(' '));
  }

  applyPintryxDropSetPriceFromText(text) {
    const priceContainer = this.querySelector('[ref="priceContainer"][data-pintryx-drop-set-price="true"]');
    if (!priceContainer || !text) return;

    const edition = String(text).toLowerCase();
    const isMetal =
      edition.includes('premium') ||
      edition.includes('prÃ©mium') ||
      edition.includes('full metal') ||
      edition.includes('fullmetal') ||
      edition.includes('metal') ||
      edition.includes('mÃ©tal');
    const isStandard = edition.includes('standard') || edition.includes('essential') || edition.includes('essentiel');
    if (!isMetal && !isStandard) return;

    const priceText = isMetal ? priceContainer.dataset.metalPrice : priceContainer.dataset.standardPrice;
    const compareText = isMetal ? priceContainer.dataset.metalCompare : priceContainer.dataset.standardCompare;
    const priceEl = priceContainer.querySelector('.price');
    const compareEl = priceContainer.querySelector('.compare-at-price');

    delete priceContainer.dataset.pintryxAwaitingEditionPrice;
    this.updateText(priceEl, priceText);
    this.updateText(compareEl, compareText);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
